import React, { useCallback, useEffect, useState } from 'react';
import { Button } from '../ui';
import { useStatusPolling } from './hooks/useStatusPolling';
import InscricaoService from '../../services/Inscricoes/InscricaoService';
import { Loader2, RefreshCw, ArrowRight, QrCode, Copy, Check, Clock } from 'lucide-react';

const inscricaoService = new InscricaoService();

export function PixDisplay({ inscricaoId, valorTotal, onSuccess, onError }) {
  const [pixData, setPixData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [errorMsg, setErrorMsg] = useState(null);

  const gerarPix = useCallback(async () => {
    if (!inscricaoId) return;
    setIsLoading(true);
    setErrorMsg(null);
    try {
      let deviceId = window.MP_DEVICE_SESSION_ID || null;
      if (!deviceId) {
        for (let i = 0; i < 10; i++) {
          deviceId = window.MP_DEVICE_SESSION_ID;
          if (deviceId) break;
          await new Promise((r) => setTimeout(r, 100));
        }
      }

      const payload = { id_requisicao: inscricaoId };
      if (deviceId) {
        payload.device_id = deviceId;
      }

      const response = await inscricaoService.checkoutPix(payload);
      const data = await response.json();
      if (!response.ok) {
        let msg = data.detail || data.message || data.erro || 'Erro ao gerar o código Pix.';
        if (data.code === 'invalid_email_for_sandbox' || (data.message && data.message.includes('@testuser.com'))) {
          msg = "No ambiente de testes (Sandbox) do Mercado Pago, o e-mail do pagador deve terminar com '@testuser.com' (exemplo: usuario@testuser.com).";
        }
        setErrorMsg(msg);
        onError?.({ status: response.status, data, message: msg });
      } else {
        setPixData(data);
      }
    } catch (err) {
      console.error('Erro ao gerar Pix:', err);
      setErrorMsg('Erro de conexão ao gerar o Pix. Tente novamente.');
      onError?.(err);
    } finally {
      setIsLoading(false);
    }
  }, [inscricaoId, onError]);

  useEffect(() => {
    if (!pixData && inscricaoId) {
      gerarPix();
    }
  }, [inscricaoId, pixData, gerarPix]);

  const orderId = pixData?.order_id || pixData?.id;
  const qrCodeBase64 = pixData?.qr_code_base64 || pixData?.point_of_interaction?.transaction_data?.qr_code_base64;
  const qrCodeText = pixData?.qr_code || pixData?.point_of_interaction?.transaction_data?.qr_code;
  const expiration = pixData?.expiration || pixData?.date_of_expiration;

  useStatusPolling({
    orderId: orderId,
    onStatusChange: (statusData) => {
      const status = statusData?.status_detail || statusData?.status;
      if (status === 'accredited' || status === 'CONFIRMED' || status === 'RECEIVED') {
        onSuccess?.(statusData);
      }
    },
  });

  const handleCopy = () => {
    if (qrCodeText) {
      navigator.clipboard.writeText(qrCodeText);
      setCopied(true);
      setTimeout(() => setCopied(false), 3000);
    }
  };

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center p-8 gap-3">
        <Loader2 className="w-10 h-10 animate-spin text-urb-primary" />
        <span className="text-sm text-slate-300 font-medium">Gerando QR Code Pix...</span>
      </div>
    );
  }

  if (errorMsg) {
    const isExpired = typeof errorMsg === 'string' && (errorMsg.toLowerCase().includes('expirad') || errorMsg.toLowerCase().includes('prazo'));

    return (
      <div className="flex flex-col items-center p-6 gap-4 text-center bg-urb-card border border-urb-border rounded-xl">
        <div className="w-full p-4 rounded-lg bg-red-500/10 border border-red-500/30 text-red-400 font-semibold text-sm">
          {errorMsg}
        </div>
        {isExpired ? (
          <>
            <p className="text-sm text-slate-400 m-0">
              Como o prazo expirou, sua vaga foi liberada. Faça uma nova inscrição para participar do evento.
            </p>
            <a
              href="/novas-corridas"
              className="inline-flex items-center gap-2 px-6 py-2.5 rounded-lg font-bold bg-urb-primary text-black hover:bg-urb-primary/90 transition-colors"
            >
              <span>Fazer Nova Inscrição</span>
              <ArrowRight className="w-4 h-4" />
            </a>
          </>
        ) : (
          <Button
            variant="secondary"
            onClick={gerarPix}
            className="flex items-center gap-2 border-urb-primary/40 text-urb-primary hover:bg-urb-primary/10"
          >
            <RefreshCw className="w-4 h-4" />
            Tentar Novamente
          </Button>
        )}
      </div>
    );
  }

  if (!pixData) {
    return (
      <div className="flex justify-center p-4">
        <Button
          onClick={gerarPix}
          className="flex items-center gap-2 py-3 px-6 text-base font-bold shadow-neon"
        >
          <QrCode className="w-5 h-5" />
          Gerar QR Code Pix — R$ {parseFloat(valorTotal || 0).toFixed(2)}
        </Button>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center text-center gap-4 py-2">
      <div>
        <h3 className="text-xl font-bold text-white m-0">Pague com Pix</h3>
        <p className="text-slate-400 text-xs sm:text-sm mt-1">
          Escaneie o QR Code abaixo no app do seu banco ou use a chave Copia e Cola:
        </p>
      </div>

      {qrCodeBase64 && (
        <div className="p-3 bg-white rounded-2xl shadow-xl">
          <img
            src={`data:image/png;base64,${qrCodeBase64}`}
            alt="QR Code Pix"
            className="w-52 h-52 object-contain"
          />
        </div>
      )}

      {qrCodeText && (
        <div className="w-full max-w-md flex flex-col gap-1.5 text-left">
          <label className="text-xs font-semibold text-slate-400">Código Copia e Cola:</label>
          <div className="p-2.5 rounded-lg bg-slate-900 border border-slate-800 text-xs font-mono text-slate-300 break-all select-all max-h-24 overflow-y-auto">
            {qrCodeText}
          </div>
          <Button
            onClick={handleCopy}
            className={`w-full mt-2 py-2.5 font-bold flex items-center justify-center gap-2 ${
              copied
                ? "bg-green-600 hover:bg-green-500 text-white"
                : "bg-urb-primary text-black hover:bg-urb-primary/90"
            }`}
          >
            {copied ? (
              <>
                <Check className="w-4 h-4 stroke-[3]" />
                Código Copiado!
              </>
            ) : (
              <>
                <Copy className="w-4 h-4" />
                Copiar Código Pix
              </>
            )}
          </Button>
        </div>
      )}

      {expiration && (
        <div className="flex items-center gap-1.5 text-xs text-slate-400">
          <Clock className="w-3.5 h-3.5 text-amber-400" />
          <span>Válido até: <strong>{new Date(expiration).toLocaleString('pt-BR')}</strong></span>
        </div>
      )}

      <div className="flex items-center justify-center gap-2.5 p-3 rounded-xl bg-slate-900/80 border border-slate-800 w-full max-w-md mt-1">
        <Loader2 className="w-4 h-4 animate-spin text-urb-primary" />
        <span className="text-xs font-medium text-slate-300">Aguardando confirmação do pagamento...</span>
      </div>
    </div>
  );
}

export default PixDisplay;
